import { cx } from "$src/lib/utils";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size as floatingSize,
  useFloating,
} from "@floating-ui/react";
import { ChevronDown } from "lucide-react";
import {
  type ComponentProps,
  type ReactNode,
  type RefObject,
  useRef,
  useState,
} from "react";
import Skeleton from "react-loading-skeleton";
import { useEventListener, useOnClickOutside } from "usehooks-ts";

import { FloatingLabel } from "../floating-label/floating-label";
import { NavMenu } from "../nav-menu/nav-menu";
import styles from "./dropdown.module.css";

export { DropdownItem } from "./lib/dropdown-item/dropdown-item";
export { DropdownSection } from "./lib/dropdown-section/dropdown-section";
export { DropdownSubmenu } from "./lib/dropdown-submenu/dropdown-submenu";

export type DropdownProps = {
  /** Label of the dropdown */
  label?: string | ReactNode;
  /** Optional selected value alongside label */
  selected?: string;
  /** Whether dropdown is open */
  open: boolean;
  /** Whether dropdown is in a loading state */
  loading?: boolean;
  /** Whether to be compact mode */
  compact?: boolean;
  /** Whether to display invalid style */
  invalid?: boolean;
  /** Optional error message */
  errorMessage?: React.ReactNode;
  /** Whether dropdown is disabled */
  disabled?: boolean;
  /** Visual theme of dropdown */
  theme?: "select" | "pill" | "inline" | "button" | "button-secondary";
  /** Called when dropdown requests open */
  onChange(state: boolean): void;
  /* Option to hide or show chevron */
  chevronVisible?: boolean;
  /* Choose which side the dropdown displays on */
  align?: "left" | "right" | "center";
  /** Whether height is constrailed — HIDES OVERFLOW */
  constrained?: boolean;
  /** Whether width is fixed to trigger width */
  fixedWidth?: boolean;
  /** Optional information or help text */
  informationText?: string;
  /** Max width of label */
  maxWidth?: string;
  /** Render the menu through a portal so it escapes scroll/overflow ancestors */
  portal?: boolean;
  /** Vertical offset between trigger and menu, in px (default 12) */
  menuOffset?: number;
} & Omit<ComponentProps<"div">, "onChange" | "onSelect">;

/**
 * @component
 * Generic dropdown menu
 */
export const ControlledDropdown = ({
  open,
  invalid,
  errorMessage,
  disabled,
  label,
  selected,
  compact,
  constrained,
  informationText,
  theme,
  loading,
  onChange,
  chevronVisible = true,
  fixedWidth,
  align = "center",
  maxWidth,
  className,
  children,
  portal,
  menuOffset = 12,
  ...props
}: DropdownProps) => {
  const container = useRef<HTMLElement>(null);

  const floating = useFloating({
    open,
    onOpenChange: onChange,
    strategy: "fixed",
    placement: align === "left" ? "bottom-end" : align === "right" ? "bottom-start" : "bottom-start",
    middleware: [
      offset(menuOffset),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      ...(fixedWidth
        ? [
            floatingSize({
              apply({ rects, elements }) {
                Object.assign(elements.floating.style, {
                  width: `${rects.reference.width}px`,
                });
              },
            }),
          ]
        : []),
    ],
    whileElementsMounted: autoUpdate,
  });

  // These are excluded from coverage because the hooks themselves are well tested
  /* v8 ignore next */
  // https://github.com/juliencrn/usehooks-ts/pull/680
  useOnClickOutside(container as RefObject<HTMLElement>, () => onChange(false));
  /* v8 ignore next */
  useEventListener("keydown", (e) => {
    e.key === "Escape" && onChange(false);
  });

  return (
    <div
      className={cx(
        styles.container,
        open && styles.active,
        disabled === true && styles.disabled,
        className,
      )}
      ref={container as any}
      data-disabled={disabled}
      {...props}
    >
      <div
        ref={portal ? floating.refs.setReference : undefined}
        className={cx(
          styles["dropdown-trigger"],
          theme && styles[theme],
          compact && styles.compact,
          open && styles.active,
          invalid && styles.error,
        )}
        onClick={() => !disabled && onChange(!open)}
      >
        <div
          className={cx(
            styles["trigger-label"],
            chevronVisible && styles["with-caret"],
            maxWidth && styles["label-maxwidth"],
          )}
          style={{ maxWidth }}
        >
          {loading ? (
            <Skeleton width="10ch" />
          ) : (
            <>
              {theme === "select" ? (
                <FloatingLabel label={label ?? ""} value={selected} />
              ) : (
                selected ?? label
              )}
            </>
          )}
        </div>
        {!disabled && !loading && chevronVisible && (
          <ChevronDown
            data-testid="dropdown-chevron"
            className={styles["trigger-icon"]}
            data-x-hidden-from-screenshot
          />
        )}
      </div>
      {informationText && (
        <div
          className={cx(
            open && styles.hidden,
            styles["information-text"],
            disabled && styles.disabled,
          )}
        >
          {informationText}
        </div>
      )}
      {invalid && errorMessage && (
        <div className={styles["error-message"]}>{errorMessage}</div>
      )}
      <NavMenu
        open={open}
        constrained={constrained}
        fixedWidth={fixedWidth}
        align={align}
        className={styles["dropdown-menu"]}
        portal={portal}
        ref={portal ? (floating.refs.setFloating as any) : undefined}
        style={portal ? floating.floatingStyles : undefined}
      >
        {children}
      </NavMenu>
    </div>
  );
};

export const Dropdown = ({
  children,
  ...props
}: Omit<DropdownProps, "open" | "onChange">) => {
  const [open, setOpen] = useState(false);
  return (
    <ControlledDropdown open={open} onChange={setOpen as any} {...props}>
      {children}
    </ControlledDropdown>
  );
};
